import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  GetEnforcementIntentDetailData,
  GetEnforcementIntentDetailVars,
  GET_ENFORCEMENT_INTENT_DETAIL,
} from "common/graphql/queries/GetEnforcementIntentDetail";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ImageCarousel } from "@app.automotus.io/components/ImageCarousel";
import type { CarouselSlide } from "@app.automotus.io/components/ImageCarousel";
import Grid from "@mui/material/Grid";
import SessionDetailsItem from "@app.automotus.io/components/SessionDetailsItem";
import { convertDateTimeToString, convertTimeToString } from "common/format";
import { getViolationTypeLabel } from "common/format/violationType";
import CitationTransactionRateBreakdown from "@app.automotus.io/components/CitationTransactionRateBreakdown";
import ViolationPenalty from "./ViolationPenalty";
import { firstSessionSlideTitle } from "./firstSessionSlideTitle";
import NoticeStatusHeader from "./NoticeStatusHeader/NoticeStatusHeader";
import NoticeActionsHeader from "./ActionsHeader/ActionsHeader";
import {
  APPLY_ENFORCEMENT_INTENT_CORRECTION,
  ApplyEnforcementIntentCorrectionData,
  ApplyEnforcementIntentCorrectionVars,
  UPDATE_ENFORCEMENT_INTENT_DUE_AT,
  UPDATE_ENFORCEMENT_INTENT_VEHICLE_MAKE,
  UpdateEnforcementIntentDueAtData,
  UpdateEnforcementIntentDueAtVars,
  UpdateEnforcementIntentVehicleMakeData,
  UpdateEnforcementIntentVehicleMakeVars,
  EnforcementPolicyOption,
} from "common/graphql";
import Box from "@mui/material/Box";
import ConcurrentEditingAlert from "@curbsuite/components/ConcurrentEditingAlert";
import VehicleMakeSessionDetailsItem, { VehicleMakeFormValues } from "./VehicleMakeSessionDetailsItem";
import VehicleSessionDetailsItem, { VehicleSessionDetailsItemFormValues } from "./VehicleSessionDetailsItem";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import CourtDateSessionDetailsItem, { CourtDateSessionDetailsItemFormValues } from "./CourtDateSessionDetailsItem";
import { useFeatureFlags, useSessionManager, useUserProfile } from "@app.automotus.io/components/hooks";
import PDFLinkButton from "./PDFLinkButton";
import Container from "@mui/material/Container";
import ZoneReferenceImageDialog from "./ZoneReferenceImageDialog";
import EnforcementPolicySelect from "./EnforcementPolicySelect";
import OperatorSelect from "./OperatorSelect";
import OperatorAssignmentItem from "./OperatorAssignmentItem";
import { AssignableOperator } from "common/graphql/queries/GetAssignableEnforcementIntentOperators";
import {
  enforcementIntentSimpleStatus,
  EnforcementIntentSource,
} from "common/graphql/fragments/enforcementIntentSummary";
import { FEATURE_FLAGS } from "@curbsuite/constants";
import LiveSessionBadge from "./LiveSessionBadge";
import { resolveLiveSessionState } from "./LiveSessionBadge/liveSessionState";
import { getAccountFeatures } from "@curbsuite/utils/userProfile";

// The confirmation stamp for the still-in-violation badge, tagged with the
// notice id it was confirmed for (this route survives navigation between
// notices, so a stamp must never vouch for a different notice's cached data).
interface LiveDataStamp {
  forId: string | undefined;
  at: Date;
}

/**
 * Title for the image-carousel slide backed by a session's `startImageUrl`, which differs by the
 * enforcement intent's source.
 *
 * An officer_input (manual) citation has no park, so there is no "start" to capture -- but `api`
 * still writes the officer's violation photo into this same `start_image_url` column (shared
 * plumbing across every session type, deliberately not renamed alongside the officer_manual ->
 * officer_input rename, AUT-5834/AUT-5830). Automated (camera) intents keep the real "Park Start"
 * meaning, since that column really does hold the session's opening frame for them.
 *
 * @param source Value of `enforcement_intent.source`, if known.
 */
const startImageSlideTitle = (source?: EnforcementIntentSource | null): string =>
  source === "officer_input" ? "Violation" : "Park Start";

export const Notice: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    isCurrentUserOldest,
    editingUserFullName,
    isInitialLoadInProgress: sessionManagerLoading,
    currentUserSessionConfirmed,
  } = useSessionManager(id);

  const { isEnabled } = useFeatureFlags();
  const { userProfile } = useUserProfile();
  const { isAirportTripCharges } = getAccountFeatures(userProfile);

  // Stamped when a network fetch completes (initial load and each poll) — the
  // still-in-violation badge shows it so an officer never acts on silently
  // stale data (AUT-5502). The stamp carries the notice id it was confirmed
  // for: this route keeps the component mounted across notices, so a stamp
  // from notice A must not vouch for notice B's cached data. Deriving the
  // match at render time (rather than resetting in an effect) means a
  // navigated-to notice is badge-less from its very first frame until its own
  // network response lands.
  const [liveDataStamp, setLiveDataStamp] = useState<LiveDataStamp | null>(null);

  const { data, refetch, networkStatus, stopPolling, startPolling } = useQuery<
    GetEnforcementIntentDetailData,
    GetEnforcementIntentDetailVars
  >(GET_ENFORCEMENT_INTENT_DETAIL, {
    variables: { id: id || "" },
    // The badge must clear within one interval of the session ending.
    // 30s matches the violation-notification poll cadence.
    pollInterval: 30_000,
    // Render cached data immediately but always confirm over the network —
    // onCompleted (and therefore the badge's as-of stamp) fires only on the
    // network response, never on the cache emission.
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onCompleted: () => setLiveDataStamp({ forId: id, at: new Date() }),
  });

  // notifyOnNetworkStatusChange makes polls and refetches flip `loading` too;
  // only a fetch with no data yet (initial load, or new variables after
  // navigating between notices) should skeleton the page.
  const intentLoading = networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables;
  const loading = sessionManagerLoading || intentLoading || !currentUserSessionConfirmed;

  const intent = data?.enforcementIntent;
  const session = intent?.sessions[0];

  // A closed session never reopens, so once nothing is live there is nothing
  // left to poll for — but stopPolling is permanent for the query instance,
  // and this component survives navigation between notices, so a live notice
  // opened after a closed one must start polling again explicitly.
  useEffect(() => {
    if (!intent || !liveDataStamp || liveDataStamp.forId !== id) {
      return;
    }
    const asOf = liveDataStamp.at;
    if (intent.voidedAt || resolveLiveSessionState(intent.sessions, asOf.getTime()).kind === "none") {
      stopPolling();
    } else {
      startPolling(30_000);
    }
  }, [intent, liveDataStamp, id, stopPolling, startPolling]);

  const [zoneReferenceDialogOpen, setZoneReferenceDialogOpen] = useState(false);
  const [selectedEnforcementPolicy, setSelectedEnforcementPolicy] = useState<EnforcementPolicyOption | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<AssignableOperator | null>(null);

  const handleSelectEnforcementPolicy = (policyId: string) => {
    setSelectedEnforcementPolicy(intent?.policy.enforcementPolicyOptions.find((o) => o.id === policyId) ?? null);
  };

  const isPendingReview = intent?.reviews.some((review) => review.status === "pending");
  // Operator assignment (AUT-5032/AUT-5033) applies only to flagged, unpermitted-vehicle
  // intents. transaction.account is just "the account the session is billed to", so
  // without this gate an ordinary paid session would surface its payer under the
  // Operator heading on citation types that have no operator at all.
  const isOperatorFieldApplicable =
    isEnabled(FEATURE_FLAGS.NOTICE_REVIEW_OPERATOR_ASSIGNMENT_IS_ENABLED) &&
    intent?.violationType === "unpermitted_vehicle";

  const transaction = intent?.sessions[0]?.transaction;
  // Assignment is one-time, so an assigned operator is always read-only, on every
  // status -- voided included.
  const assignedOperator = isOperatorFieldApplicable ? transaction?.account ?? null : null;
  const assignedAtLocal =
    intent && assignedOperator && transaction?.assignedToAccountAt
      ? utcToZonedTime(transaction.assignedToAccountAt, intent.timezone)
      : null;

  // Assign during review (AUT-5032). Optional — the reviewer can issue without
  // assigning. The selection persists only when the reviewer hits Issue (rides the
  // review mutation).
  const isOperatorAssignmentEnabled = isOperatorFieldApplicable && !assignedOperator && isPendingReview;
  // Assign after review (AUT-5033): issued, non-voided notices still unassigned.
  const isPostReviewOperatorAssignmentVisible =
    isOperatorFieldApplicable &&
    !assignedOperator &&
    !isPendingReview &&
    !intent?.voidedAt &&
    (intent ? enforcementIntentSimpleStatus[intent.status] === "issued" : false);

  // License plate information can only be edited if the intent has not been submitted for RO lookup
  const isLicensePlateEditEnabled = isPendingReview && isCurrentUserOldest && !intent?.roLookupSubmittedAt;
  // Vehicle make information can be edited as long as there is a pending review;
  const isVehicleMakeEditEnabled = isPendingReview && isCurrentUserOldest;
  // Due at can only be edited if there is a pending review and the workflow config allows it
  const isDueAtEditEnabled =
    isPendingReview && isCurrentUserOldest && Boolean(intent?.policy.workflowIntegrationConfig?.isDueDateEditEnabled);

  const [updateMake] = useMutation<UpdateEnforcementIntentVehicleMakeData, UpdateEnforcementIntentVehicleMakeVars>(
    UPDATE_ENFORCEMENT_INTENT_VEHICLE_MAKE,
  );
  const handleEditVehicleMake = async ({ make: newMake }: VehicleMakeFormValues) => {
    if (intent && isVehicleMakeEditEnabled) {
      await updateMake({
        variables: {
          id: id || "",
          make: newMake.toLowerCase(),
          // We use a regex to ensure that only the first instance of the make is replaced,
          // which is needed for cases where the default value of "other_other" is used for the model
          model: intent.vehicleModel.replace(new RegExp(intent.vehicleMake), newMake.toLowerCase()),
        },
      });
    }
  };

  const [updateDueAt] = useMutation<UpdateEnforcementIntentDueAtData, UpdateEnforcementIntentDueAtVars>(
    UPDATE_ENFORCEMENT_INTENT_DUE_AT,
  );
  const handleEditCourtDate = async ({ courtDate }: CourtDateSessionDetailsItemFormValues) => {
    if (intent && isDueAtEditEnabled) {
      await updateDueAt({
        variables: {
          id: intent.id,
          // TODO: consider trimming/setting timezone
          dueAt: courtDate,
        },
      });
    }
  };

  const [applyCorrection] = useMutation<ApplyEnforcementIntentCorrectionData, ApplyEnforcementIntentCorrectionVars>(
    APPLY_ENFORCEMENT_INTENT_CORRECTION,
  );

  // Split across three plain statements: the repo's eslint-plugin-react-hooks
  // version misparses a comparison inside a ternary test (`a && a.b === c ? …`)
  // anywhere in the component as a conditional hook call.
  const stampMatchesNotice = liveDataStamp?.forId === id;
  const currentStamp = stampMatchesNotice ? liveDataStamp : null;
  const liveDataAsOf = currentStamp ? currentStamp.at : null;
  const handleEditLicensePlate = async ({
    licensePlateNumber,
    licensePlateRegion,
  }: VehicleSessionDetailsItemFormValues) => {
    if (intent && isLicensePlateEditEnabled) {
      await applyCorrection({
        variables: {
          intentId: id || "",
          licensePlateNumber,
          licensePlateRegion,
        },
      });
      await refetch();
    }
  };

  // Display the license plate capture date in the local timezone
  const licensePlateCaptureDate =
    intent && session
      ? utcToZonedTime(session.licensePlateCapturedAt || session.startedAt, intent.timezone)
      : new Date(0);

  const sessionStartedAtLocal = intent && session ? utcToZonedTime(session.startedAt, intent.timezone) : undefined;
  const sessionEndedAtLocal =
    intent && session && session.endedAt ? utcToZonedTime(session.endedAt, intent.timezone) : undefined;
  const violationAtLocal =
    intent && session && session.violationAt ? utcToZonedTime(session.violationAt, intent.timezone) : undefined;

  const officerReviewedAt = intent?.reviews.find((review) => review.type === "officer")?.reviewedAt;
  const officerReviewedAtLocal =
    intent && officerReviewedAt ? utcToZonedTime(officerReviewedAt, intent.timezone) : undefined;

  const noticeSlides: CarouselSlide[] = (
    [
      session?.licensePlateImageUrl
        ? {
            title: firstSessionSlideTitle(session.type),
            date: licensePlateCaptureDate,
            imageSrc: session.licensePlateImageUrl,
          }
        : null,
      sessionStartedAtLocal && session?.startImageUrl
        ? { title: startImageSlideTitle(intent?.source), date: sessionStartedAtLocal, imageSrc: session.startImageUrl }
        : null,
      violationAtLocal && session?.violationImageUrl
        ? { title: "Violation", date: violationAtLocal, imageSrc: session.violationImageUrl }
        : null,
      sessionEndedAtLocal && session?.endImageUrl
        ? { title: "Park End", date: sessionEndedAtLocal, imageSrc: session.endImageUrl }
        : null,
    ] as Array<CarouselSlide | null>
  ).filter((v): v is CarouselSlide => v !== null);

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flexGrow: 1,
        py: { xs: 1.5, sm: 3.5 },
      }}
    >
      <Stack spacing={{ xs: 1.5, sm: 3 }}>
        {!isCurrentUserOldest && currentUserSessionConfirmed && !sessionManagerLoading && isPendingReview && (
          <ConcurrentEditingAlert editingUserFullName={editingUserFullName} itemLabel="notice" />
        )}
        <NoticeActionsHeader
          intent={intent}
          loading={loading}
          isReviewEnabled={isPendingReview && isCurrentUserOldest && !loading}
          refetch={refetch}
          selectedEnforcementPolicyId={selectedEnforcementPolicy?.id ?? null}
          selectedOperatorId={selectedOperator?.accountId ?? null}
        />
        <LiveSessionBadge intent={intent} asOf={liveDataAsOf} />
        <NoticeStatusHeader loading={loading} intent={intent} />
        <Paper>
          <ImageCarousel loading={loading} slides={noticeSlides} />
        </Paper>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={1}>
            <Typography variant={"h5"} fontWeight={400}>
              Notice Details
            </Typography>
            <Grid container rowSpacing={2}>
              <SessionDetailsItem
                loading={loading}
                title={"Violation Date"}
                content={formatInTimeZone(
                  new Date(isAirportTripCharges ? session?.startedAt || 0 : session?.violationAt || 0),
                  intent?.timezone || "UTC",
                  "M/d/yyyy",
                )}
                skeletonWidth={104}
              />
              <SessionDetailsItem
                loading={loading}
                title={"Violation Time"}
                content={`${convertTimeToString(
                  utcToZonedTime(
                    isAirportTripCharges ? session?.startedAt || 0 : session?.violationAt || 0,
                    intent?.timezone || "UTC",
                  ),
                )}`}
                skeletonWidth={166}
              />
              <SessionDetailsItem
                loading={loading}
                title={"Location"}
                skeletonWidth={111}
                contentChildren={
                  <Typography
                    variant="body1"
                    onClick={() => setZoneReferenceDialogOpen(true)}
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {session?.streetAddress || "_"}
                  </Typography>
                }
              />
              <SessionDetailsItem
                loading={loading}
                title={"City"}
                content={`${intent?.payeeAccount.customer.addressCity}, ${intent?.payeeAccount.customer.addressState}`}
                skeletonWidth={112}
              />
              <VehicleSessionDetailsItem
                loading={loading}
                licensePlateNumber={intent?.licensePlateNumber || ""}
                licensePlateRegion={intent?.licensePlateRegion || ""}
                skeletonWidth={104}
                onSubmitEdit={isLicensePlateEditEnabled ? handleEditLicensePlate : undefined}
              />
              {isEnabled(FEATURE_FLAGS.NOTICE_REVIEW_INCLUDE_VEHICLE_MAKE) && (
                <VehicleMakeSessionDetailsItem
                  loading={loading}
                  vehicleMake={intent?.vehicleMake || ""}
                  vehicleMakeAlias={intent?.vehicleMakeAlias?.[0]}
                  isVehicleMakeMappingRequired={intent?.policy.workflowIntegrationConfig?.isVehicleMakeMappingRequired}
                  skeletonWidth={166}
                  onSubmitEdit={isVehicleMakeEditEnabled ? handleEditVehicleMake : undefined}
                />
              )}
              <SessionDetailsItem
                loading={loading}
                title={"Violation Type"}
                content={getViolationTypeLabel(intent?.violationType ?? "", userProfile?.account?.id)}
                skeletonWidth={112}
              />
              {intent?.policy.routingParameter === "user_input" && isPendingReview ? (
                <Grid item xs={6} sm={3} sx={{ pr: 2 }}>
                  <Typography variant="subtitle1" fontWeight={500}>
                    Violation Code
                  </Typography>
                  <EnforcementPolicySelect
                    enforcementPolicyOptions={intent.policy.enforcementPolicyOptions}
                    value={selectedEnforcementPolicy?.id ?? null}
                    onChange={handleSelectEnforcementPolicy}
                  />
                </Grid>
              ) : (
                <SessionDetailsItem
                  loading={loading}
                  title={"Violation Code"}
                  content={intent?.code ?? "-"}
                  skeletonWidth={112}
                />
              )}
              {intent?.policy.routingParameter === "user_input" && isPendingReview && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Base amount and total penalty will be determined when the notice is issued.
                  </Typography>
                </Grid>
              )}
              {intent && isOperatorAssignmentEnabled && (
                <Grid item xs={6} sm={3} sx={{ pr: 2 }}>
                  <Typography variant="subtitle1" fontWeight={500}>
                    Operator
                  </Typography>
                  <OperatorSelect
                    intentId={intent.id}
                    value={selectedOperator}
                    onChange={setSelectedOperator}
                    disabled={!isCurrentUserOldest}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Optional. Assignment is one-time and bills the operator when the notice is issued.
                  </Typography>
                </Grid>
              )}
              {intent && isPostReviewOperatorAssignmentVisible && (
                <OperatorAssignmentItem intent={intent} refetch={refetch} />
              )}
              {intent && assignedOperator && (
                <SessionDetailsItem
                  loading={loading}
                  title={"Operator"}
                  skeletonWidth={112}
                  contentChildren={
                    // SessionDetailsItem lays contentChildren out in a horizontal flex row,
                    // so stack the name and assignment time in a column of our own.
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant={"body1"}>{assignedOperator.organizationName}</Typography>
                      {assignedAtLocal && (
                        <Typography variant={"caption"} color={"text.secondary"}>
                          Assigned {convertDateTimeToString(assignedAtLocal)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              )}
              {intent?.policy.workflowIntegrationConfig?.isDueDateRequired && (
                <CourtDateSessionDetailsItem
                  loading={loading}
                  courtDate={intent?.dueAt ? new Date(intent.dueAt) : undefined}
                  skeletonWidth={166}
                  onSubmitEdit={isDueAtEditEnabled ? handleEditCourtDate : undefined}
                />
              )}
              {intent?.roLookupSubmittedAt && intent?.officerName && (
                <SessionDetailsItem
                  loading={loading}
                  title={"Issued By"}
                  content={intent.officerName}
                  skeletonWidth={112}
                />
              )}
              {officerReviewedAtLocal && (
                <SessionDetailsItem
                  loading={loading}
                  title={"Reviewed At"}
                  content={convertDateTimeToString(officerReviewedAtLocal)}
                  skeletonWidth={166}
                />
              )}
            </Grid>
            {isPendingReview &&
              (intent?.policy.workflowIntegrationConfig?.officerInputNoticeNumberRegex ||
                intent?.policy.workflowIntegrationConfig?.isDueDateEditEnabled) && (
                <Box>
                  <Typography>
                    IMPORTANT: make sure that the vehicle make, court date, and citation number match the information
                    displayed on your handheld device
                  </Typography>
                </Box>
              )}
          </Stack>
        </Paper>
        <CitationTransactionRateBreakdown
          loading={loading}
          policyRateDetails={session?.park?.policyRateDetails || []}
        />
        {intent?.noticeType !== "undecided" && (
          <ViolationPenalty intent={intent} loading={loading} isAirportTripCharges={isAirportTripCharges} />
        )}
        <PDFLinkButton url={intent?.noticeContentUrl} noticeNumber={intent?.noticeNumber} />
        <ZoneReferenceImageDialog
          open={zoneReferenceDialogOpen}
          onClose={() => setZoneReferenceDialogOpen(false)}
          cameraId={session?.cameraId || ""}
        />
      </Stack>
    </Container>
  );
};

export default Notice;
